import React, { useState, useEffect, useRef } from "react";
import queryString from "query-string";
import { CSSTransition } from "react-transition-group";
import { Form, Alert } from "reactstrap";
import { useLocalization } from "../../containers/I18nProvider";
import { WithCaptcha } from '../../containers/captcha';
import logo from './img/logo.png';

const EyeIcon = ({ open }) => open ? (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
) : (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const LoginForm = ({ api, location, TurnstileComponent, ...props }) => {
  const nodeRef = useRef();
  const { translate, changeDefaultLocale } = useLocalization();
  const [onLogin, setOnLogin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState({ open: false, message: "" });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const parameters = queryString.parse(location.search);

  useEffect(() => {
    changeDefaultLocale('es');
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setOnLogin(true);
    setError({ open: false, message: "" });

    api.userPassLogin(
      { username, password, cp_convert: parameters.cp_convert },
      parameters
    )
      .then((response) => {
        setOnLogin(false);
        window.location = parameters.redirect_uri + "?" + queryString.stringify(response);
      })
      .catch((error) => {
        const _translate = translate(error.errorCode);
        let message = translate("RequestLoginErrorMessage");
        if (_translate) {
          message = _translate;
        } else if (error.details) {
          message = error.details;
        }
        setOnLogin(false);
        setError({ open: true, message });
      });
  };

  return (
    <CSSTransition
      in={showForm}
      timeout={300}
      classNames="formshow"
      nodeRef={nodeRef}
      unmountOnExit
    >
      <div className="ameport-wrapper" ref={nodeRef}>
        <div className="ameport-card">
          <div className="ameport-logo">
            <img src={logo} alt="Ameport" />
          </div>

          <Form onSubmit={onSubmit}>
            <div className="ameport-field">
              <label className="ameport-label">{translate("LabelUserName")}</label>
              <div className="ameport-input-group">
                <span className="ameport-input-icon"><UserIcon /></span>
                <input
                  className="ameport-input"
                  type="text"
                  disabled={onLogin}
                  onChange={e => setUsername(e.target.value)}
                  required
                  tabIndex={1}
                  autoCorrect="off"
                  autoCapitalize="none"
                  placeholder={translate("PlaceholderUserName")}
                />
              </div>
            </div>

            <div className="ameport-field">
              <label className="ameport-label">{translate("LabelPassword")}</label>
              <div className="ameport-input-group">
                <span className="ameport-input-icon"><LockIcon /></span>
                <input
                  className="ameport-input"
                  type={showPassword ? "text" : "password"}
                  disabled={onLogin}
                  onChange={e => setPassword(e.target.value)}
                  required
                  tabIndex={2}
                  placeholder={translate("PlaceholderPassword")}
                />
                <button
                  type="button"
                  className="ameport-eye-btn"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            <div className="ameport-forgot-password">
              <a
                href="https://reset.ameport.org.ar/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {translate("ForgotPassword")}
              </a>
            </div>

            <div className="ameport-captcha">
              <TurnstileComponent setTurnstileSuccess={props.setTurnstileSuccess} />
            </div>

            <button
              type="submit"
              className="ameport-btn"
              disabled={onLogin || !props.turnstileSuccess}
            >
              {onLogin ? (
                <span className="ameport-btn-loading" />
              ) : (
                translate("ButtonEnter")
              )}
            </button>
          </Form>

          <Alert
            color="danger"
            isOpen={error.open}
            toggle={() => setError({ open: false, message: '' })}
            className="ameport-alert"
          >
            {error.message}
          </Alert>
        </div>
      </div>
    </CSSTransition>
  );
};

export default WithCaptcha(LoginForm, { useInsideForm: true });
